import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Insights as InsightsIcon,
  Refresh as RefreshIcon,
  RocketLaunch as RocketIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { surgeReadinessAPI } from '../services/api';
import { translateSectorName } from '../utils/sectorTranslations';

const C = {
  green: '#10b981',
  blue: '#3b82f6',
  amber: '#f59e0b',
  red: '#ef4444',
  slate: '#64748b',
  purple: '#8b5cf6',
};

const fmtPct = (value, digits = 2) => (value != null ? `${value >= 0 ? '+' : ''}${Number(value).toFixed(digits)}%` : '—');
const fmtVnd = (value, language = 'en') => {
  if (value == null) return '—';
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  return Number(value).toLocaleString(locale, { maximumFractionDigits: 0 });
};

const fmtVndCompact = (value) => {
  if (value == null) return '—';
  const numeric = Number(value);
  const abs = Math.abs(numeric);
  if (abs >= 1e12) return `${(numeric / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(numeric / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(numeric / 1e6).toFixed(1)}M`;
  return Math.round(numeric).toString();
};

const scoreColor = (score) => {
  if (score >= 90) return C.green;
  if (score >= 80) return C.blue;
  if (score >= 70) return C.amber;
  return C.slate;
};

const toReadableLabel = (value) => {
  if (!value) return '—';
  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const toTranslationKey = (value) => String(value || '')
  .replace(/[^a-zA-Z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .toUpperCase();

const formatRegimeLabel = (t, regime) => {
  if (!regime) return '—';
  return t(`dashboard.surgeReadiness.marketRegimes.${toTranslationKey(regime)}`, {
    defaultValue: toReadableLabel(regime),
  });
};

const formatSetupType = (t, setupType) => {
  if (!setupType) return '—';
  return t(`dashboard.surgeReadiness.setupTypes.${toTranslationKey(setupType)}`, {
    defaultValue: toReadableLabel(setupType),
  });
};

const formatEntrySignal = (t, entrySignal) => {
  if (!entrySignal) return '—';
  return t(`dashboard.surgeReadiness.entrySignals.${toTranslationKey(entrySignal)}`, {
    defaultValue: toReadableLabel(entrySignal),
  });
};

const formatOutcomeLabel = (t, outcome) => {
  if (!outcome) return '—';
  return t(`dashboard.surgeReadiness.outcomeLabels.${toTranslationKey(outcome)}`, {
    defaultValue: toReadableLabel(outcome),
  });
};

const formatOutcomeStatus = (t, status) => {
  if (!status) return t('dashboard.surgeReadiness.journal.pending');
  return t(`dashboard.surgeReadiness.outcomeStatuses.${toTranslationKey(status)}`, {
    defaultValue: toReadableLabel(status),
  });
};

const formatSector = (t, sector) => {
  if (!sector) return '—';
  return translateSectorName(t, sector);
};

const getPrioritySummary = (row, t) => {
  const score = Number(row?.score || 0);
  if (score >= 210) return { label: t('dashboard.surgeReadiness.priority.veryHigh'), tone: C.green };
  if (score >= 190) return { label: t('dashboard.surgeReadiness.priority.high'), tone: C.blue };
  if (score >= 170) return { label: t('dashboard.surgeReadiness.priority.watch'), tone: C.amber };
  return { label: t('dashboard.surgeReadiness.priority.observe'), tone: C.slate };
};

const getReadinessSummary = (row, t) => {
  if (row?.entry_signal) {
    return {
      label: t('dashboard.surgeReadiness.readiness.readySignal'),
      sub: formatEntrySignal(t, row.entry_signal),
      tone: C.green,
    };
  }
  const readiness = Number(row?.entry_readiness || 0);
  if (readiness >= 70) {
    return { label: t('dashboard.surgeReadiness.readiness.almostReady'), sub: t('dashboard.surgeReadiness.readiness.readyPercent', { value: readiness }), tone: C.blue };
  }
  if (readiness >= 40) {
    return { label: t('dashboard.surgeReadiness.readiness.monitoring'), sub: t('dashboard.surgeReadiness.readiness.readyPercent', { value: readiness }), tone: C.amber };
  }
  return { label: t('dashboard.surgeReadiness.readiness.waiting'), sub: t('dashboard.surgeReadiness.readiness.readyPercent', { value: readiness }), tone: C.slate };
};

const getContextSummary = (row, t) => {
  const breadth = Number(row?.breadth_score_sector || 0);
  if (breadth >= 50) {
    return { label: t('dashboard.surgeReadiness.context.supportiveSector'), sub: formatSector(t, row?.sector), tone: C.green };
  }
  if (breadth >= 35) {
    return { label: t('dashboard.surgeReadiness.context.neutralSector'), sub: formatSector(t, row?.sector), tone: C.amber };
  }
  return { label: t('dashboard.surgeReadiness.context.weakSector'), sub: formatSector(t, row?.sector), tone: C.slate };
};

const getRiskSummary = (row, t) => {
  const warningCount = (row?.warnings || []).length;
  if (warningCount === 0) {
    return { label: t('dashboard.surgeReadiness.risk.low'), sub: t('dashboard.surgeReadiness.risk.noMajorWarning'), tone: C.green };
  }
  if (warningCount <= 2) {
    return { label: t('dashboard.surgeReadiness.risk.medium'), sub: row.warnings[0], tone: C.amber };
  }
  return { label: t('dashboard.surgeReadiness.risk.high'), sub: row.warnings[0], tone: C.red };
};

const getReasonSummary = (row, t) => {
  const reasons = row?.reasons || [];
  if (!reasons.length) return t('dashboard.surgeReadiness.reason.waitingMoreConfirmation');
  return reasons.slice(0, 2).join(' · ');
};

const regimeColor = (regime) => ({
  RISK_ON: C.green,
  RECOVERY: C.blue,
  SIDEWAY_WEAK: C.amber,
  RISK_OFF: C.red,
}[regime] || C.slate);

const outcomeColor = (label) => {
  if (label === 'WIN') return C.green;
  if (label === 'LOSS') return C.red;
  if (label === 'NEUTRAL') return C.amber;
  return C.slate;
};

const getCalibrationEstimate = (score, calibration) => {
  if (!calibration?.bins?.length || score == null) {
    return null;
  }

  const numericScore = Number(score);
  const bins = calibration.bins || [];
  const matched = bins.find((bin) => numericScore >= Number(bin.score_min) && numericScore <= Number(bin.score_max));

  if (matched) {
    return matched;
  }

  return bins.reduce((closest, bin) => {
    if (!closest) return bin;
    const currentMid = (Number(bin.score_min) + Number(bin.score_max)) / 2;
    const bestMid = (Number(closest.score_min) + Number(closest.score_max)) / 2;
    return Math.abs(currentMid - numericScore) < Math.abs(bestMid - numericScore) ? bin : closest;
  }, null);
};

const confidenceTone = (confidence) => {
  if (confidence === 'HIGH') return C.green;
  if (confidence === 'MEDIUM') return C.blue;
  if (confidence === 'LOW') return C.amber;
  return C.slate;
};

const LATEST_CACHE_TTL_MS = 60 * 1000;

let latestScanCache = {
  key: null,
  data: null,
  promise: null,
  ts: 0,
};

const tickerDetailCache = new Map();

const getLatestScan = async (minScore, force = false) => {
  const key = JSON.stringify({ minScore });
  const now = Date.now();

  if (!force && latestScanCache.key === key && latestScanCache.data && now - latestScanCache.ts < LATEST_CACHE_TTL_MS) {
    return latestScanCache.data;
  }

  if (!force && latestScanCache.key === key && latestScanCache.promise) {
    return latestScanCache.promise;
  }

  latestScanCache.key = key;
  latestScanCache.promise = surgeReadinessAPI
    .getLatest({ limit: 50, min_score: minScore })
    .then((res) => {
      latestScanCache = {
        key,
        data: res.data,
        promise: null,
        ts: Date.now(),
      };
      return res.data;
    })
    .catch((err) => {
      latestScanCache = {
        key: null,
        data: null,
        promise: null,
        ts: 0,
      };
      throw err;
    });

  return latestScanCache.promise;
};

const SectionCard = ({ children, sx = {} }) => (
  <Box sx={{
    bgcolor: '#fff',
    border: '1px solid #e7edf5',
    borderRadius: 1.5,
    overflow: 'hidden',
    boxShadow: '0 6px 20px rgba(15, 23, 42, 0.035), 0 1px 2px rgba(15, 23, 42, 0.035)',
    ...sx,
  }}>
    {children}
  </Box>
);

const SurgeReadinessPageSkeleton = () => (
  <Box sx={{ p: 0, width: '100%', maxWidth: '100%', bgcolor: 'transparent' }}>
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 0 }}>
          <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 1.5 }} />
          <Box>
            <Skeleton variant="text" width={220} height={28} />
            <Skeleton variant="text" width={340} height={20} />
          </Box>
        </Box>
        <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
          <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: 1.5 }} />
          <Skeleton variant="rounded" width={100} height={32} sx={{ borderRadius: 1.5 }} />
          <Skeleton variant="rounded" width={88} height={32} sx={{ borderRadius: 1.5 }} />
          <Skeleton variant="rounded" width={118} height={32} sx={{ borderRadius: 1.5 }} />
          <Skeleton variant="rounded" width={92} height={32} sx={{ borderRadius: 1.5 }} />
        </Stack>
      </Box>

      <SectionCard>
        <Box sx={{ px: 2, py: 1.35 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(8, minmax(0, 1fr))' }, gap: 0 }}>
            {Array.from({ length: 8 }).map((_, idx) => (
              <Box key={`kpi-skeleton-${idx}`} sx={{ px: 1.5, py: 1, borderRight: idx < 7 ? '1px solid #f1f5f9' : 'none' }}>
                <Skeleton variant="text" width="72%" height={12} />
                <Skeleton variant="text" width="52%" height={26} />
                <Skeleton variant="text" width="68%" height={14} />
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Skeleton variant="circular" width={6} height={6} />
            <Skeleton variant="text" width={360} height={18} />
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.6 }}>
              <Skeleton variant="rounded" width={86} height={20} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rounded" width={100} height={20} sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
        </Box>
      </SectionCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.25fr) minmax(420px, 0.85fr)' }, gap: 1.25, alignItems: 'start' }}>
        <SectionCard sx={{ minWidth: 0 }}>
          <Box sx={{ px: 1.8, py: 1.25, borderBottom: '1px solid #edf2f8', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25 }}>
            <Box>
              <Skeleton variant="text" width={84} height={14} />
              <Skeleton variant="text" width={170} height={22} />
              <Skeleton variant="text" width={300} height={18} />
            </Box>
            <Skeleton variant="text" width={140} height={18} />
          </Box>
          <TableContainer sx={{ width: '100%', overflowX: 'visible' }}>
            <Table size="small" sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { lineHeight: 1.2 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <TableCell key={`scanner-head-skeleton-${idx}`} sx={tableHeadCellSx}>
                      <Skeleton variant="text" width={idx === 5 ? 80 : 64} height={14} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 8 }).map((_, rowIdx) => (
                  <TableRow key={`scanner-row-skeleton-${rowIdx}`}>
                    <TableCell sx={tableBodyCellSx}>
                      <Skeleton variant="text" width={40} height={18} />
                      <Skeleton variant="text" width={88} height={14} />
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Skeleton variant="text" width={96} height={18} />
                      <Skeleton variant="text" width={92} height={14} />
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Skeleton variant="text" width={92} height={18} />
                      <Skeleton variant="text" width={82} height={14} />
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Skeleton variant="text" width={104} height={18} />
                      <Skeleton variant="text" width={90} height={14} />
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Skeleton variant="text" width={80} height={18} />
                      <Skeleton variant="text" width={76} height={14} />
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Skeleton variant="text" width={120} height={18} />
                      <Skeleton variant="text" width={140} height={14} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>

        <Stack spacing={1.25} sx={{ minWidth: 0 }}>
          <SectionCard>
            <Box sx={{ px: 1.8, py: 1.25, borderBottom: '1px solid #edf2f8' }}>
              <Skeleton variant="text" width={74} height={14} />
              <Skeleton variant="text" width={150} height={22} />
              <Skeleton variant="text" width={280} height={18} />
            </Box>
            <Box sx={{ p: 0.95 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 0.55 }}>
                {Array.from({ length: 4 }).map((_, idx) => (
                  <Box key={`context-top-skeleton-${idx}`} sx={{ p: 0.82, borderRadius: 1.5, border: '1px solid #e9eef6', bgcolor: '#fff' }}>
                    <Skeleton variant="text" width="65%" height={11} />
                    <Skeleton variant="text" width="54%" height={20} />
                    <Skeleton variant="text" width="72%" height={13} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 0.7, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 0.65 }}>
                {Array.from({ length: 3 }).map((_, panelIdx) => (
                  <Box key={`context-panel-skeleton-${panelIdx}`} sx={{ border: '1px solid #e9eef6', borderRadius: 1.5, bgcolor: '#fff', overflow: 'hidden' }}>
                    <Box sx={{ px: 0.85, py: 0.55, borderBottom: '1px solid #e9eef6' }}>
                      <Skeleton variant="text" width="58%" height={12} />
                    </Box>
                    <Box sx={{ px: 0.85, py: 0.25 }}>
                      {Array.from({ length: 4 }).map((_, rowIdx) => (
                        <Box key={`context-panel-row-skeleton-${panelIdx}-${rowIdx}`} sx={{ py: 0.45, borderBottom: rowIdx < 3 ? '1px solid #eff4fa' : 'none' }}>
                          <Skeleton variant="text" width="85%" height={15} />
                          <Skeleton variant="text" width="62%" height={12} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </SectionCard>

          <SectionCard>
            <Box sx={{ px: 1.8, py: 1.25, borderBottom: '1px solid #edf2f8' }}>
              <Skeleton variant="text" width={82} height={14} />
              <Skeleton variant="text" width={190} height={22} />
              <Skeleton variant="text" width={300} height={18} />
            </Box>
            <Box sx={{ p: 0.95 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 0.55 }}>
                {Array.from({ length: 4 }).map((_, idx) => (
                  <Box key={`validation-top-skeleton-${idx}`} sx={{ minWidth: 0 }}>
                    <Skeleton variant="text" width="60%" height={11} />
                    <Skeleton variant="text" width="52%" height={20} />
                    <Skeleton variant="text" width="68%" height={13} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 0.72, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' }, gap: 1 }}>
                <Box>
                  <Skeleton variant="text" width={90} height={12} />
                  <Box sx={{ borderTop: '1px solid #edf2f8', borderBottom: '1px solid #edf2f8' }}>
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <Box key={`validation-left-row-skeleton-${idx}`} sx={{ py: 0.46, borderBottom: idx < 2 ? '1px solid #f1f5fa' : 'none' }}>
                        <Skeleton variant="text" width="90%" height={15} />
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ pt: 0.68, mt: 0.3, borderTop: '1px solid #edf2f8' }}>
                    <Skeleton variant="text" width={104} height={12} />
                    <Skeleton variant="rounded" width="100%" height={62} sx={{ borderRadius: 1 }} />
                  </Box>
                </Box>
                <Box sx={{ borderLeft: { lg: '1px solid #edf2f8' }, pl: { lg: 0.4 } }}>
                  <Skeleton variant="text" width={90} height={12} />
                  <Box sx={{ borderTop: '1px solid #edf2f8', borderBottom: '1px solid #edf2f8' }}>
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <Box key={`validation-right-row-skeleton-${idx}`} sx={{ py: 0.44, borderBottom: idx < 3 ? '1px solid #f1f5fa' : 'none' }}>
                        <Skeleton variant="text" width="95%" height={15} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </SectionCard>
        </Stack>
      </Box>

      <SectionCard>
        <Box sx={{ px: 1.8, py: 1.25, borderBottom: '1px solid #edf2f8' }}>
          <Skeleton variant="text" width={70} height={14} />
          <Skeleton variant="text" width={170} height={22} />
          <Skeleton variant="text" width={310} height={18} />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {Array.from({ length: 9 }).map((_, idx) => (
                  <TableCell key={`journal-head-skeleton-${idx}`} sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', py: 1.1, borderBottom: '1px solid #e2e8f0' }}>
                    <Skeleton variant="text" width={idx < 2 ? 56 : 44} height={14} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 6 }).map((_, rowIdx) => (
                <TableRow key={`journal-row-skeleton-${rowIdx}`}>
                  {Array.from({ length: 9 }).map((__, colIdx) => (
                    <TableCell key={`journal-cell-skeleton-${rowIdx}-${colIdx}`} sx={{ py: 0.85, borderBottom: '1px solid #f1f5f9' }}>
                      <Skeleton variant="text" width={colIdx === 1 ? 88 : 52} height={15} />
                      {colIdx === 1 ? <Skeleton variant="text" width={120} height={12} /> : null}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Stack>
  </Box>
);

const SectionHeader = ({ eyebrow, title, description, action }) => (
  <Box sx={{ px: 1.8, py: 1.25, borderBottom: '1px solid #edf2f8', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25 }}>
    <Box>
      {eyebrow ? (
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: '#9aa8bd', mb: 0.28 }}>
          {eyebrow}
        </Typography>
      ) : null}
      <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.18 }}>
        {title}
      </Typography>
      {description ? (
        <Typography sx={{ fontSize: '0.71rem', color: '#6b7d95', mt: 0.22, lineHeight: 1.32 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
    {action}
  </Box>
);

const CompactInfoRow = ({ title, primary, secondary, accent = '#0f172a', trailing = null }) => (
  <Box
    sx={{
      py: 0.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 0.8,
      borderBottom: '1px solid #eff4fa',
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#8ea0b8', fontWeight: 700, textTransform: 'none', letterSpacing: 0, flexShrink: 0 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, color: accent, lineHeight: 1.15 }}>
          {primary}
        </Typography>
      </Box>
      {secondary ? (
        <Typography sx={{ fontSize: '0.66rem', color: '#90a0b6', lineHeight: 1.2 }}>
          {secondary}
        </Typography>
      ) : null}
    </Box>
    {trailing}
  </Box>
);

const DensePanel = ({ title, primary = null, secondary = null, accent = '#0f172a', trailing = null, children = null, sx = {} }) => (
  <Box
    sx={{
      p: 0.82,
      borderRadius: 1.5,
      border: '1px solid #e9eef6',
      bgcolor: '#ffffff',
      minWidth: 0,
      ...sx,
    }}
  >
    {children ? (
      <>
        <Typography sx={{ fontSize: '0.62rem', color: '#95a4b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.56 }}>
          {title}
        </Typography>
        {children}
      </>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.6rem', color: '#95a4b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.36, mb: 0.2 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: accent, lineHeight: 1.1 }}>
            {primary}
          </Typography>
          {secondary ? (
            <Typography sx={{ fontSize: '0.66rem', color: '#6e7f95', mt: 0.16, lineHeight: 1.26 }}>
              {secondary}
            </Typography>
          ) : null}
        </Box>
        {trailing}
      </Box>
    )}
  </Box>
);

const StripMetric = ({ label, value, sub, color = '#0f172a' }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.38, mb: 0.22 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.92rem', fontWeight: 900, color, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {sub ? (
      <Typography sx={{ fontSize: '0.66rem', color: '#64748b', mt: 0.14, lineHeight: 1.25 }}>
        {sub}
      </Typography>
    ) : null}
  </Box>
);

const tableHeadCellSx = {
  fontWeight: 800,
  color: '#64748b',
  fontSize: '0.7rem',
  py: 0.72,
  px: 1,
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap',
};

const tableBodyCellSx = {
  py: 0.58,
  px: 1,
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'middle',
};

const SurgeDetailDrawerSkeleton = () => (
  <Box sx={{ border: '1px solid #dfe7f2', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#fff' }}>
    <Box sx={{ px: 1.4, pt: 1.25, pb: 1.15 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Skeleton variant="text" width="78%" height={28} />
          <Skeleton variant="text" width="52%" height={18} />
        </Box>
        <Skeleton variant="rounded" width={40} height={28} sx={{ borderRadius: 1.2 }} />
      </Stack>

      <Box sx={{ mt: 0.85, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.8 }}>
        {Array.from({ length: 4 }).map((_, idx) => (
          <Box key={`drawer-top-metric-skeleton-${idx}`}>
            <Skeleton variant="text" width="60%" height={12} />
            <Skeleton variant="text" width="56%" height={22} />
          </Box>
        ))}
      </Box>
    </Box>

    <Divider />

    <Box sx={{ p: 1.1, bgcolor: '#f8fbff' }}>
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Box
              key={`drawer-grid-metric-skeleton-${idx}`}
              sx={{
                px: 0.85,
                py: 0.75,
                minWidth: 0,
                borderRight: idx % 3 !== 2 ? '1px solid #edf2f8' : 'none',
                borderBottom: idx < 3 ? '1px solid #edf2f8' : 'none',
              }}
            >
              <Skeleton variant="text" width="64%" height={11} />
              <Skeleton variant="text" width="54%" height={20} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    <Divider />

    <Box sx={{ px: 1.4, py: 1.1 }}>
      <Skeleton variant="text" width={66} height={20} />
      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
        {Array.from({ length: 10 }).map((_, idx) => (
          <Skeleton key={`drawer-reason-chip-skeleton-${idx}`} variant="rounded" width={idx % 3 === 0 ? 150 : 120} height={28} sx={{ borderRadius: 1.1 }} />
        ))}
      </Stack>
    </Box>

    <Divider />

    <Box sx={{ px: 1.4, py: 1.1 }}>
      <Skeleton variant="text" width={82} height={20} />
      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
        {Array.from({ length: 2 }).map((_, idx) => (
          <Skeleton key={`drawer-warning-chip-skeleton-${idx}`} variant="rounded" width={idx === 0 ? 140 : 220} height={28} sx={{ borderRadius: 1.1 }} />
        ))}
      </Stack>
    </Box>

    <Divider />

    <Box sx={{ px: 1.4, py: 1.1 }}>
      <Skeleton variant="text" width={120} height={20} />
      <Box sx={{ borderTop: '1px solid #edf2f8', borderBottom: '1px solid #edf2f8', mt: 0.5 }}>
        {Array.from({ length: 6 }).map((_, idx) => (
          <Box
            key={`drawer-recent-row-skeleton-${idx}`}
            sx={{
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              borderBottom: idx < 5 ? '1px solid #f1f5f9' : 'none',
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Skeleton variant="text" width={96} height={18} />
              <Skeleton variant="text" width="62%" height={14} />
            </Box>
            <Skeleton variant="rounded" width={86} height={22} sx={{ borderRadius: 1 }} />
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

function SurgeDetailDrawer({ ticker, open, onClose, calibration, initialLatest = null }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !ticker) {
      return;
    }

    let cancelled = false;
    const cachedDetail = tickerDetailCache.get(ticker) || null;
    const seededDetail = cachedDetail || (initialLatest?.ticker === ticker
      ? {
        ticker,
        found: true,
        latest: initialLatest,
        price_history: [],
      }
      : null);

    setDetail(seededDetail);
    setLoading(!seededDetail);
    setError(null);

    if (cachedDetail?.price_history?.length) {
      return () => {
        cancelled = true;
      };
    }

    const load = async () => {
      try {
        const res = await surgeReadinessAPI.getTicker(ticker, 120);
        if (cancelled) {
          return;
        }
        tickerDetailCache.set(ticker, res.data);
        setDetail((prev) => ({
          ...res.data,
          latest: res.data?.latest || prev?.latest || initialLatest || null,
          price_history: res.data?.price_history || prev?.price_history || [],
        }));
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Failed to load surge ticker detail', err);
        if (!seededDetail) {
          setError(t('dashboard.surgeReadiness.drawer.errors.loadDetail'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, ticker, initialLatest, t]);

  const latest = detail?.latest;
  const drawerCalibration = useMemo(
    () => getCalibrationEstimate(latest?.score, calibration),
    [latest?.score, calibration],
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 560, maxWidth: '100vw', height: '100%', bgcolor: '#fff' }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
              {ticker || t('dashboard.surgeReadiness.table.ticker')}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>
              {t('dashboard.surgeReadiness.drawer.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          {loading && !detail?.latest ? <SurgeDetailDrawerSkeleton /> : null}

          {!loading && error ? <Alert severity="error">{error}</Alert> : null}

          {!loading && !error && detail && !detail.found ? (
            <Alert severity="warning">{t('dashboard.surgeReadiness.drawer.errors.tickerNotFound')}</Alert>
          ) : null}

          {!loading && latest ? (
            <Box sx={{ border: '1px solid #dfe7f2', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#fff' }}>
              <Box sx={{ px: 1.4, pt: 1.25, pb: 1.15 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>
                      {latest.company_name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                      {formatSector(t, latest.sector) || t('dashboard.surgeReadiness.drawer.unknown')} | {formatSetupType(t, latest.surge_setup_type)}
                    </Typography>
                  </Box>
                  <Chip
                    label={latest.grade}
                    sx={{
                      fontWeight: 800,
                      bgcolor: alpha(scoreColor(latest.score), 0.12),
                      color: scoreColor(latest.score),
                    }}
                  />
                </Stack>

                <Box sx={{ mt: 0.85, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.8 }}>
                  <StripMetric label={t('dashboard.surgeReadiness.metrics.score')} value={latest.score} color={scoreColor(latest.score)} />
                  <StripMetric label={t('dashboard.surgeReadiness.metrics.opportunity')} value={latest.opportunity_score} color={C.purple} />
                  <StripMetric label={t('dashboard.surgeReadiness.metrics.entryReadiness')} value={`${latest.entry_readiness}%`} color={latest.entry_ready ? C.green : C.amber} />
                  <StripMetric label={t('dashboard.surgeReadiness.metrics.market')} value={formatRegimeLabel(t, latest.market_regime)} color={regimeColor(latest.market_regime)} />
                </Box>

                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.55, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={drawerCalibration ? t('dashboard.surgeReadiness.calibration.edgeValue', { value: drawerCalibration.edge_10d }) : t('dashboard.surgeReadiness.calibration.edgePending')}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(drawerCalibration ? (Number(drawerCalibration.edge_10d) >= 0 ? C.green : C.red) : C.slate, 0.12),
                      color: drawerCalibration ? (Number(drawerCalibration.edge_10d) >= 0 ? C.green : C.red) : C.slate,
                    }}
                  />
                  <Chip
                    size="small"
                    label={drawerCalibration
                      ? t(`dashboard.surgeReadiness.calibration.confidence.${String(drawerCalibration.confidence || 'UNKNOWN').toLowerCase()}`, {
                        defaultValue: String(drawerCalibration.confidence || 'UNKNOWN'),
                      })
                      : t('dashboard.surgeReadiness.calibration.confidence.limited')}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(confidenceTone(drawerCalibration?.confidence), 0.12),
                      color: confidenceTone(drawerCalibration?.confidence),
                    }}
                  />
                  {drawerCalibration?.samples ? (
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 700 }}>
                      {t('dashboard.surgeReadiness.calibration.samples', { count: drawerCalibration.samples })}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              <Divider />

              <Box sx={{ p: 1.1, bgcolor: '#f8fbff' }}>
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                    {[
                      { label: t('dashboard.surgeReadiness.drawer.metrics.close'), value: fmtVnd(latest.close, i18n.language), color: '#1e293b' },
                      { label: t('dashboard.surgeReadiness.drawer.metrics.valueMA20'), value: fmtVndCompact(latest.value_ma20), color: '#1e293b' },
                      { label: t('dashboard.surgeReadiness.drawer.metrics.rsVsIndex'), value: fmtPct(latest.rs_vs_index, 2), color: latest.rs_vs_index > 0 ? C.green : C.red },
                      { label: t('dashboard.surgeReadiness.drawer.metrics.rsVsSector'), value: fmtPct(latest.rs_vs_sector, 2), color: latest.rs_vs_sector > 0 ? C.green : C.red },
                      { label: t('dashboard.surgeReadiness.drawer.metrics.pullback'), value: fmtPct((latest.pullback_depth || 0) * 100, 2), color: '#1e293b' },
                      { label: t('dashboard.surgeReadiness.drawer.metrics.compression'), value: latest.compression_ratio != null ? latest.compression_ratio.toFixed(2) : '—', color: '#1e293b' },
                    ].map((item, index) => (
                      <Box
                        key={item.label}
                        sx={{
                          px: 0.85,
                          py: 0.75,
                          minWidth: 0,
                          borderRight: index % 3 !== 2 ? '1px solid #edf2f8' : 'none',
                          borderBottom: index < 3 ? '1px solid #edf2f8' : 'none',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.24, lineHeight: 1.08 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ mt: 0.24, fontSize: '0.9rem', fontWeight: 800, color: item.color, lineHeight: 1.1, wordBreak: 'break-word' }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ px: 1.4, py: 1.1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155', mb: 1 }}>
                  {t('dashboard.surgeReadiness.drawer.reasons')}
                </Typography>
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                  {(latest.reasons || []).map((item) => (
                    <Chip key={item} size="small" label={item} sx={{ bgcolor: alpha(C.green, 0.1), color: C.green, fontWeight: 700 }} />
                  ))}
                </Stack>
              </Box>

              {latest.warnings?.length ? (
                <>
                  <Divider />
                  <Box sx={{ px: 1.4, py: 1.1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155', mb: 1 }}>
                      {t('dashboard.surgeReadiness.drawer.warnings')}
                    </Typography>
                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                      {latest.warnings.map((item) => (
                        <Chip key={item} size="small" label={item} sx={{ bgcolor: alpha(C.amber, 0.14), color: C.amber, fontWeight: 700 }} />
                      ))}
                    </Stack>
                  </Box>
                </>
              ) : null}

              <Divider />

              <Box sx={{ px: 1.4, py: 1.1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155', mb: 0.85 }}>
                  {t('dashboard.surgeReadiness.drawer.recentPriceCheckpoints')}
                </Typography>
                <Box sx={{ borderTop: '1px solid #edf2f8', borderBottom: '1px solid #edf2f8' }}>
                  {(detail.price_history || []).slice(-8).reverse().map((row, index, list) => (
                    <Box
                      key={row.date}
                      sx={{
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        borderBottom: index < list.length - 1 ? '1px solid #f1f5f9' : 'none',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>{row.date}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                          {t('dashboard.surgeReadiness.drawer.priceRow', { close: fmtVnd(row.close, i18n.language), ma20: fmtVnd(row.ma20, i18n.language) })}
                        </Typography>
                      </Box>
                      {row.entry_signal ? (
                        <Chip size="small" label={formatEntrySignal(t, row.entry_signal)} sx={{ fontWeight: 700, bgcolor: alpha(C.blue, 0.12), color: C.blue, flexShrink: 0 }} />
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}

export default function SurgeReadiness() {
  const { t } = useTranslation();
  const [mainLoading, setMainLoading] = useState(true);
  const [sideLoading, setSideLoading] = useState(true);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sideError, setSideError] = useState(null);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [journal, setJournal] = useState([]);
  const [outcomes, setOutcomes] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [minScore, setMinScore] = useState(72);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [backtest, setBacktest] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const openTickerDrawer = useCallback((ticker, candidate = null) => {
    setSelectedTicker(ticker);
    setSelectedCandidate(candidate);
  }, []);

  const closeTickerDrawer = useCallback(() => {
    setSelectedTicker(null);
    setSelectedCandidate(null);
  }, []);

  const loadPrimaryData = useCallback(async (force = false) => {
    setMainLoading(true);
    setError(null);
    try {
      const latest = await getLatestScan(minScore, force);
      setData(latest);
    } catch (err) {
      console.error('Failed to load surge readiness latest scan', err);
      setError(t('dashboard.surgeReadiness.errors.loadLiveScan'));
    } finally {
      setMainLoading(false);
    }
  }, [minScore, t]);

  const loadSecondaryData = useCallback(async () => {
    setSideLoading(true);
    setSideError(null);
    try {
      const results = await Promise.allSettled([
        surgeReadinessAPI.getHistory(20),
        surgeReadinessAPI.getJournal({ days: 45, limit: 12 }),
        surgeReadinessAPI.getOutcomes(120),
        surgeReadinessAPI.getCalibration(180, 6),
      ]);

      const [historyRes, journalRes, outcomesRes, calibrationRes] = results;
      if (historyRes.status === 'fulfilled') {
        setHistory(historyRes.value.data.days || []);
      }
      if (journalRes.status === 'fulfilled') {
        setJournal(journalRes.value.data.rows || []);
      }
      if (outcomesRes.status === 'fulfilled') {
        setOutcomes(outcomesRes.value.data || null);
      }
      if (calibrationRes.status === 'fulfilled') {
        setCalibration(calibrationRes.value.data || null);
      }

      if (results.every((item) => item.status === 'rejected')) {
        setSideError(t('dashboard.surgeReadiness.errors.loadSecondary'));
      }
    } catch (err) {
      console.error('Failed to load surge readiness secondary data', err);
      setSideError(t('dashboard.surgeReadiness.errors.loadSecondary'));
    } finally {
      setSideLoading(false);
    }
  }, [t]);

  const refreshAll = useCallback(async (force = false) => {
    await Promise.all([
      loadPrimaryData(force),
      loadSecondaryData(),
    ]);
  }, [loadPrimaryData, loadSecondaryData]);

  useEffect(() => {
    refreshAll(false);
  }, [refreshAll]);

  const runBacktest = useCallback(async () => {
    setBacktestLoading(true);
    setBacktest(null);
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 60);
      const payload = {
        start_date: start.toISOString().slice(0, 10),
        end_date: end.toISOString().slice(0, 10),
        top_n: 8,
        min_score: minScore,
      };
      const res = await surgeReadinessAPI.backtest(payload);
      setBacktest(res.data);
    } catch (err) {
      console.error('Failed to backtest surge readiness', err);
      setBacktest({ error: t('dashboard.surgeReadiness.errors.backtest') });
    } finally {
      setBacktestLoading(false);
    }
  }, [minScore, t]);

  const createSnapshot = useCallback(async () => {
    setSnapshotLoading(true);
    setActionMessage(null);
    try {
      const res = await surgeReadinessAPI.snapshot(null, minScore);
      setActionMessage(t('dashboard.surgeReadiness.actions.snapshotSaved', {
        date: res.data.signal_date,
        count: res.data.stored_count,
      }));
      await refreshAll(true);
    } catch (err) {
      console.error('Failed to create snapshot', err);
      setActionMessage(t('dashboard.surgeReadiness.errors.snapshot'));
    } finally {
      setSnapshotLoading(false);
    }
  }, [refreshAll, minScore, t]);

  const trackOutcomes = useCallback(async () => {
    setTrackingLoading(true);
    setActionMessage(null);
    try {
      const res = await surgeReadinessAPI.trackOutcomes();
      setActionMessage(t('dashboard.surgeReadiness.actions.trackUpdated', {
        updated: res.data.updated,
        completed: res.data.completed,
      }));
      await loadSecondaryData();
    } catch (err) {
      console.error('Failed to track outcomes', err);
      setActionMessage(t('dashboard.surgeReadiness.errors.track'));
    } finally {
      setTrackingLoading(false);
    }
  }, [loadSecondaryData, t]);

  const candidates = useMemo(() => data?.candidates || [], [data?.candidates]);
  const summary = data?.summary || {};
  const market = data?.market || {};
  const topSectors = useMemo(() => data?.top_sectors || [], [data?.top_sectors]);
  const sectorBreadth = useMemo(() => data?.sector_breadth || [], [data?.sector_breadth]);
  const outcomeSummary = outcomes?.summary || {};
  const latestStoredDate = journal[0]?.signal_date || history[0]?.trade_date || null;
  const fallbackCandidates = useMemo(
    () => (latestStoredDate ? journal.filter((row) => row.signal_date === latestStoredDate) : []),
    [journal, latestStoredDate],
  );
  const displayCandidates = candidates.length ? candidates : fallbackCandidates;
  const displayScanDate = data?.scan_date || latestStoredDate || '—';
  const displayMarketRegime = market.regime || history[0]?.market_regime || 'UNKNOWN';
  const displaySummary = candidates.length ? summary : {
    candidate_count: history[0]?.candidate_count || fallbackCandidates.length || 0,
    grade_a_count: history[0]?.grade_a_count || fallbackCandidates.filter((row) => row.grade === 'A').length || 0,
    avg_score: history[0]?.avg_score || 0,
    avg_opportunity_score: 0,
  };
  const strongestCandidate = displayCandidates[0] || null;
  const sectorBreadthMap = useMemo(
    () => new Map(sectorBreadth.map((row) => [row.sector, row])),
    [sectorBreadth],
  );
  const sectorInsights = useMemo(
    () => topSectors.slice(0, 4).map((row) => ({
      ...row,
      breadth: sectorBreadthMap.get(row.sector),
    })),
    [topSectors, sectorBreadthMap],
  );
  const compactHistory = history.slice(0, 8);
  const entryReadyCount = useMemo(
    () => displayCandidates.filter((row) => row.entry_ready || row.entry_signal).length,
    [displayCandidates],
  );
  const detectorAlignedCount = useMemo(
    () => displayCandidates.filter((row) => (row.detector_final_score || 0) >= 0.75).length,
    [displayCandidates],
  );
  const setupMix = useMemo(() => {
    const count = new Map();
    displayCandidates.forEach((row) => {
      const key = row.surge_setup_type || 'OTHER';
      count.set(key, (count.get(key) || 0) + 1);
    });
    return [...count.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [displayCandidates]);
  const warningHotspots = useMemo(
    () => displayCandidates
      .filter((row) => (row.warnings || []).length)
      .slice(0, 4)
      .map((row) => ({
        ticker: row.ticker,
        warningCount: row.warnings.length,
        firstWarning: row.warnings[0],
      })),
    [displayCandidates],
  );
  const warningTotalFlags = useMemo(
    () => warningHotspots.reduce((sum, row) => sum + row.warningCount, 0),
    [warningHotspots],
  );
  const baselineCalibrationWinRate = calibration?.baseline_win_rate_10d || 0;

  const showFullPageSkeleton = mainLoading && sideLoading && !data && !history.length && !journal.length;

  if (showFullPageSkeleton) {
    return <SurgeReadinessPageSkeleton />;
  }

  return (
    <Box sx={{ p: 0, width: '100%', maxWidth: '100%', bgcolor: 'transparent' }}>
      <Stack spacing={1.5}>
        {/* ── COMPACT HEADER WITH INLINE CONTROLS ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 0 }}>
            <Box sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              flexShrink: 0,
              background: `linear-gradient(135deg, ${C.purple} 0%, ${alpha(C.purple, 0.82)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 3px 10px ${alpha(C.purple, 0.25)}`,
              color: '#fff',
            }}>
              <RocketIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.1rem', letterSpacing: '-0.01em' }}>
                  {t('dashboard.surgeReadiness.title')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 0.9, py: 0.25, borderRadius: 1.2 }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: C.purple }} />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569' }}>
                    {displayScanDate}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={formatRegimeLabel(t, displayMarketRegime)}
                  sx={{ fontWeight: 800, bgcolor: alpha(regimeColor(displayMarketRegime), 0.12), color: regimeColor(displayMarketRegime), border: `1px solid ${alpha(regimeColor(displayMarketRegime), 0.18)}`, height: 22, fontSize: '0.7rem' }}
                />
              </Box>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', mt: 0.1 }}>
                {t('dashboard.surgeReadiness.subtitle')}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
            <TextField
              label={t('dashboard.surgeReadiness.filters.minScore')}
              type="number"
              size="small"
              value={minScore}
              onChange={(event) => setMinScore(Number(event.target.value || 0))}
              sx={{
                width: 90,
                '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 1.5, fontWeight: 700, fontSize: '0.82rem' },
                '& .MuiInputLabel-root': { fontSize: '0.78rem' },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryIcon />}
              onClick={createSnapshot}
              disabled={snapshotLoading}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, borderColor: '#dbe4f0', color: '#475569', bgcolor: '#fff', fontSize: '0.78rem' }}
            >
              {snapshotLoading ? t('dashboard.surgeReadiness.actions.saving') : t('dashboard.surgeReadiness.actions.snapshot')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<InsightsIcon />}
              onClick={trackOutcomes}
              disabled={trackingLoading}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, borderColor: '#dbe4f0', color: '#475569', bgcolor: '#fff', fontSize: '0.78rem' }}
            >
              {trackingLoading ? t('dashboard.surgeReadiness.actions.tracking') : t('dashboard.surgeReadiness.actions.track')}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<ScienceIcon />}
              onClick={runBacktest}
              disabled={backtestLoading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, bgcolor: C.purple, '&:hover': { bgcolor: '#7c3aed' }, fontSize: '0.78rem' }}
            >
              {backtestLoading ? t('dashboard.surgeReadiness.actions.running') : t('dashboard.surgeReadiness.actions.backtest60d')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refreshAll(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, color: '#64748b', borderColor: '#e2e8f0', '&:hover': { bgcolor: '#f8fafc' }, fontSize: '0.78rem' }}
            >
              {t('dashboard.surgeReadiness.actions.refresh')}
            </Button>
          </Stack>
        </Box>

        {/* ── KPI METRICS STRIP ── */}
        <SectionCard>
          <Box sx={{ px: 2, py: 1.35 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(8, minmax(0, 1fr))' }, gap: 0 }}>
              {[
                { label: t('dashboard.surgeReadiness.kpis.candidates'), value: displaySummary.candidate_count || 0, sub: t('dashboard.surgeReadiness.kpis.gradeA', { count: displaySummary.grade_a_count || 0 }), color: C.purple },
                { label: t('dashboard.surgeReadiness.kpis.avgScore'), value: displaySummary.avg_score || 0, sub: t('dashboard.surgeReadiness.kpis.opportunity', { value: displaySummary.avg_opportunity_score || 0 }), color: C.blue },
                { label: t('dashboard.surgeReadiness.kpis.readyToBuy'), value: entryReadyCount, sub: t('dashboard.surgeReadiness.kpis.listPercent', { value: displayCandidates.length ? Math.round((entryReadyCount / displayCandidates.length) * 100) : 0 }), color: C.green },
                { label: t('dashboard.surgeReadiness.kpis.detectorAligned'), value: detectorAlignedCount, sub: t('dashboard.surgeReadiness.kpis.detectorThreshold'), color: C.blue },
                { label: t('dashboard.surgeReadiness.kpis.tracked'), value: outcomeSummary.completed || 0, sub: t('dashboard.surgeReadiness.kpis.snapshots', { count: outcomeSummary.total_snapshots || 0 }), color: C.slate },
                { label: t('dashboard.surgeReadiness.kpis.win10d'), value: `${outcomeSummary.win_rate_10d || 0}%`, sub: t('dashboard.surgeReadiness.kpis.avgReturn', { value: outcomeSummary.avg_ret_10d || 0 }), color: (outcomeSummary.win_rate_10d || 0) >= 50 ? C.green : C.red },
                { label: t('dashboard.surgeReadiness.kpis.maxGain10d'), value: `${outcomeSummary.avg_max_gain_10d || 0}%`, sub: t('dashboard.surgeReadiness.kpis.followThrough'), color: C.green },
                { label: t('dashboard.surgeReadiness.kpis.drawdown10d'), value: `${outcomeSummary.avg_max_drawdown_10d || 0}%`, sub: t('dashboard.surgeReadiness.kpis.riskAfterSignal'), color: C.red },
              ].map((item, idx, arr) => (
                <Box key={item.label} sx={{ px: 1.5, py: 1, borderRight: idx < arr.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.3 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontSize: '1.08rem', fontWeight: 900, color: item.color, lineHeight: 1.1 }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.64rem', color: '#64748b', mt: 0.18 }}>{item.sub}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: regimeColor(displayMarketRegime), flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                {(market.reasons || []).slice(0, 2).join(' | ') || t('dashboard.surgeReadiness.marketFallback')}
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.6 }}>
                <Chip label={t('dashboard.surgeReadiness.kpis.trendScore', { value: market.trend_score || 0 })} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', border: '1px solid #dbe4f0', color: '#334155', height: 20, fontSize: '0.68rem' }} />
                {!data?.scan_date && latestStoredDate ? (
                  <Chip label={t('dashboard.surgeReadiness.kpis.storedDate', { date: latestStoredDate })} size="small" sx={{ fontWeight: 700, bgcolor: alpha(C.slate, 0.08), color: '#475569', border: '1px solid #dbe4f0', height: 20, fontSize: '0.68rem' }} />
                ) : null}
              </Box>
            </Box>
          </Box>
          {mainLoading ? <LinearProgress sx={{ height: 3 }} /> : null}
        </SectionCard>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {sideError ? <Alert severity="warning">{sideError}</Alert> : null}
        {actionMessage ? <Alert severity="info">{actionMessage}</Alert> : null}

        {/* ── MAIN 2-COLUMN GRID ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.25fr) minmax(420px, 0.85fr)' }, gap: 1.25, alignItems: 'start' }}>
          <SectionCard sx={{ minWidth: 0 }}>
            <SectionHeader
              eyebrow={t('dashboard.surgeReadiness.sections.scanner.eyebrow')}
              title={t('dashboard.surgeReadiness.sections.scanner.title')}
              description={t('dashboard.surgeReadiness.sections.scanner.description')}
              action={mainLoading && fallbackCandidates.length ? (
                <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                  {t('dashboard.surgeReadiness.sections.scanner.actions.showingStored')}
                </Typography>
              ) : mainLoading ? (
                <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                  {t('dashboard.surgeReadiness.sections.scanner.actions.loadingLive')}
                </Typography>
              ) : null}
            />

              <TableContainer sx={{ width: '100%', overflowX: 'visible' }}>
                <Table size="small" sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { lineHeight: 1.2 } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      {[
                        t('dashboard.surgeReadiness.table.ticker'),
                        t('dashboard.surgeReadiness.table.priority'),
                        t('dashboard.surgeReadiness.table.signal'),
                        t('dashboard.surgeReadiness.table.context'),
                        t('dashboard.surgeReadiness.table.risk'),
                        t('dashboard.surgeReadiness.table.reason'),
                      ].map((label) => (
                        <TableCell key={label} sx={tableHeadCellSx}>
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayCandidates.map((row) => (
                      <TableRow
                        key={`${row.ticker}-${row.date || row.signal_date}`}
                        hover
                        onClick={() => openTickerDrawer(row.ticker, row)}
                        sx={{
                          cursor: 'pointer',
                          '&:nth-of-type(even)': { bgcolor: '#fcfdff' },
                          '&:hover': { bgcolor: alpha(C.blue, 0.03) },
                        }}
                      >
                        <TableCell sx={{ ...tableBodyCellSx }}>
                          <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.ticker}</Typography>
                          <Typography sx={{ fontSize: '0.64rem', color: '#64748b', mt: 0.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.grade} · {formatSector(t, row.sector)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableBodyCellSx }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: getPrioritySummary(row, t).tone }}>
                            {getPrioritySummary(row, t).label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.64rem', color: '#64748b', mt: 0.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t('dashboard.surgeReadiness.table.scoreGrade', { score: Math.round(row.score || 0), grade: row.grade || '—' })}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableBodyCellSx }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: getReadinessSummary(row, t).tone }}>
                            {getReadinessSummary(row, t).label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.64rem', color: '#64748b', mt: 0.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getReadinessSummary(row, t).sub}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableBodyCellSx }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: getContextSummary(row, t).tone }}>
                            {getContextSummary(row, t).label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.64rem', color: '#64748b', mt: 0.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t('dashboard.surgeReadiness.table.contextRow', { sector: getContextSummary(row, t).sub, rs: row.rs_vs_index != null ? fmtPct((row.rs_vs_index || 0) * 100, 1) : '—' })}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableBodyCellSx }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: getRiskSummary(row, t).tone }}>
                            {getRiskSummary(row, t).label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.64rem',
                              color: '#64748b',
                              mt: 0.1,
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                            }}
                          >
                            {getRiskSummary(row, t).sub}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableBodyCellSx }}>
                          {(() => {
                            const estimate = getCalibrationEstimate(row.score, calibration);
                            const confidenceLabel = estimate
                              ? t(`dashboard.surgeReadiness.calibration.confidence.${String(estimate.confidence || 'UNKNOWN').toLowerCase()}`, {
                                defaultValue: String(estimate.confidence || 'UNKNOWN'),
                              })
                              : t('dashboard.surgeReadiness.calibration.confidence.limited');

                            return (
                              <>
                                <Typography sx={{ fontSize: '0.76rem', color: '#334155', fontWeight: 700 }}>
                                  {getReasonSummary(row, t)}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.64rem',
                                    color: estimate ? (Number(estimate.edge_10d) >= 0 ? C.green : C.red) : '#475569',
                                    whiteSpace: 'nowrap',
                                    wordBreak: 'break-word',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    fontWeight: 700,
                                  }}
                                >
                                  {estimate
                                    ? t('dashboard.surgeReadiness.calibration.rowSummary', {
                                      edge: estimate.edge_10d,
                                      confidence: confidenceLabel,
                                    })
                                    : t('dashboard.surgeReadiness.table.detectorAgreement', { score: row.detector_final_score != null ? row.detector_final_score.toFixed(2) : '—' })}
                                </Typography>
                              </>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!displayCandidates.length ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center', color: '#94a3b8' }}>
                          {mainLoading ? t('dashboard.surgeReadiness.table.loadingCandidates') : t('dashboard.surgeReadiness.table.noCandidates')}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
          </SectionCard>

          {/* RIGHT: sidebar */}
          <Stack spacing={1.25} sx={{ minWidth: 0 }}>
            <SectionCard>
              <SectionHeader
                eyebrow={t('dashboard.surgeReadiness.sections.context.eyebrow')}
                title={t('dashboard.surgeReadiness.sections.context.title')}
                description={t('dashboard.surgeReadiness.sections.context.description')}
              />
              <Box sx={{ p: 0.95 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 0.55 }}>
                  <DensePanel title={t('dashboard.surgeReadiness.contextCards.market')} primary={formatRegimeLabel(t, displayMarketRegime)} secondary={(market.reasons || []).slice(0, 1).join(' | ') || t('dashboard.surgeReadiness.contextCards.marketFallback')} accent={regimeColor(displayMarketRegime)} />
                  <DensePanel title={t('dashboard.surgeReadiness.contextCards.buyScale')} primary={`${Math.round((market.buy_scale || 0) * 100)}%`} secondary={t('dashboard.surgeReadiness.contextCards.buyScaleHint')} accent={C.purple} />
                  <DensePanel title={t('dashboard.surgeReadiness.contextCards.topTicker')} primary={strongestCandidate?.ticker || '—'} secondary={strongestCandidate ? `${getPrioritySummary(strongestCandidate, t).label}` : t('dashboard.surgeReadiness.contextCards.noLivePick')} accent={scoreColor(strongestCandidate?.score || 0)} />
                  <DensePanel title={t('dashboard.surgeReadiness.contextCards.leadingSector')} primary={formatSector(t, sectorInsights[0]?.sector)} secondary={sectorInsights[0] ? t('dashboard.surgeReadiness.contextCards.rank', { rank: sectorInsights[0].sector_rank || '—' }) : t('dashboard.surgeReadiness.contextCards.noSectorContext')} accent={C.blue} />
                </Box>

                <Box sx={{ mt: 0.7, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 0.65, alignItems: 'start' }}>
                  <Box sx={{ border: '1px solid #e9eef6', borderRadius: 1.5, bgcolor: '#fff', overflow: 'hidden' }}>
                    <Box sx={{ px: 0.85, py: 0.55, borderBottom: '1px solid #e9eef6' }}>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.42 }}>
                        {t('dashboard.surgeReadiness.contextPanels.strongSectors')}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 0.85, py: 0.2 }}>
                      {sectorInsights.slice(0, 4).map((row) => (
                        <CompactInfoRow
                          key={row.sector}
                          title={t('dashboard.surgeReadiness.contextPanels.sector')}
                          primary={t('dashboard.surgeReadiness.contextPanels.sectorRow', { rank: row.sector_rank || '—', rs: fmtPct((row.sector_rs_vs_index || 0) * 100, 1) })}
                          secondary={`${formatSector(t, row.sector)}${row.breadth ? ` · ${t('dashboard.surgeReadiness.contextPanels.aboveMA20', { value: Math.round((row.breadth.pct_above_ma20 || 0) * 100) })}` : ''}`}
                          trailing={<Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: C.blue }}>{row.breadth?.breadth_score != null ? Math.round(row.breadth.breadth_score) : '—'}</Typography>}
                        />
                      ))}
                      {!sectorInsights.length ? <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', py: 0.6 }}>{t('dashboard.surgeReadiness.contextPanels.noSectorData')}</Typography> : null}
                    </Box>
                  </Box>

                  <Box sx={{ border: '1px solid #e9eef6', borderRadius: 1.5, bgcolor: '#fff', overflow: 'hidden' }}>
                    <Box sx={{ px: 0.85, py: 0.55, borderBottom: '1px solid #e9eef6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.6 }}>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.42 }}>
                        {t('dashboard.surgeReadiness.contextPanels.setupMix')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700 }}>
                        {setupMix[0] ? t('dashboard.surgeReadiness.contextPanels.tickersCount', { count: setupMix[0][1] }) : '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 0.85, py: 0.2 }}>
                      {setupMix.slice(0, 4).map(([label, count]) => (
                        <CompactInfoRow
                          key={label}
                          title={t('dashboard.surgeReadiness.contextPanels.setup')}
                          primary={formatSetupType(t, label)}
                          secondary={t('dashboard.surgeReadiness.contextPanels.appearedCount', { count })}
                          accent="#1e293b"
                          trailing={<Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: C.blue }}>{count}</Typography>}
                        />
                      ))}
                      {!setupMix.length ? <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', py: 0.6 }}>{t('dashboard.surgeReadiness.contextPanels.noSetupMix')}</Typography> : null}
                    </Box>
                  </Box>

                  <Box sx={{ border: '1px solid #e9eef6', borderRadius: 1.5, bgcolor: '#fff', overflow: 'hidden' }}>
                    <Box sx={{ px: 0.85, py: 0.55, borderBottom: '1px solid #e9eef6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.6 }}>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.42 }}>
                        {t('dashboard.surgeReadiness.contextPanels.warningHotspots')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700 }}>
                        {t('dashboard.surgeReadiness.contextPanels.flagsCount', { count: warningTotalFlags })}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 0.85, py: 0.2 }}>
                      {warningHotspots.slice(0, 4).map((row) => (
                        <CompactInfoRow
                          key={row.ticker}
                          title={row.ticker}
                          primary={row.firstWarning}
                          secondary={t('dashboard.surgeReadiness.contextPanels.warningReview')}
                          accent={C.amber}
                          trailing={<Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: C.amber }}>{row.warningCount}</Typography>}
                        />
                      ))}
                      {!warningHotspots.length ? <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', py: 0.6 }}>{t('dashboard.surgeReadiness.contextPanels.noWarningHotspots')}</Typography> : null}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </SectionCard>

            <SectionCard>
              <SectionHeader
                eyebrow={t('dashboard.surgeReadiness.sections.validation.eyebrow')}
                title={t('dashboard.surgeReadiness.sections.validation.title')}
                description={t('dashboard.surgeReadiness.sections.validation.description')}
              />
              <Box sx={{ p: 0.95 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 0.55 }}>
                  <StripMetric label={t('dashboard.surgeReadiness.validationMetrics.tracked')} value={outcomeSummary.completed || 0} sub={t('dashboard.surgeReadiness.kpis.snapshots', { count: outcomeSummary.total_snapshots || 0 })} color={C.slate} />
                  <StripMetric label={t('dashboard.surgeReadiness.validationMetrics.win10d')} value={`${outcomeSummary.win_rate_10d || 0}%`} sub={t('dashboard.surgeReadiness.kpis.avgReturn', { value: outcomeSummary.avg_ret_10d || 0 })} color={(outcomeSummary.win_rate_10d || 0) >= 50 ? C.green : C.red} />
                  <StripMetric label={t('dashboard.surgeReadiness.validationMetrics.maxGain')} value={`${outcomeSummary.avg_max_gain_10d || 0}%`} sub={t('dashboard.surgeReadiness.kpis.followThrough')} color={C.green} />
                  <StripMetric
                    label={t('dashboard.surgeReadiness.validationMetrics.drawdown')}
                    value={`${outcomeSummary.avg_max_drawdown_10d || 0}%`}
                    sub={t('dashboard.surgeReadiness.validationMetrics.baselineWin', { value: baselineCalibrationWinRate })}
                    color={C.red}
                  />
                </Box>

                <Box sx={{ mt: 0.72, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' }, gap: 1, alignItems: 'start' }}>
                  <Stack spacing={0.85}>
                    <Box>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.42, mb: 0.38 }}>
                        {t('dashboard.surgeReadiness.validationPanels.byGrade')}
                      </Typography>
                      <Box sx={{ display: 'grid', gap: 0.42 }}>
                        {(outcomes?.by_grade || []).slice(0, 3).map((row) => {
                          const winRate = row.total ? ((row.wins_10d || 0) / row.total) * 100 : 0;
                          return (
                            <Box key={row.grade || '—'} sx={{ py: 0.5, px: 0.55, borderRadius: 1, bgcolor: '#f8fafc', display: 'grid', gridTemplateColumns: '50px 1fr auto', alignItems: 'center', gap: 0.45 }}>
                              <Typography sx={{ fontSize: '0.69rem', color: '#8ea0b8', fontWeight: 700 }}>G{row.grade || '—'}</Typography>
                              <Typography sx={{ fontSize: '0.67rem', color: '#6e7f95' }}>{t('dashboard.surgeReadiness.validationPanels.tickersCount', { count: row.total || 0 })}</Typography>
                              <Typography sx={{ fontSize: '0.84rem', fontWeight: 900, color: winRate >= 50 ? C.green : C.red }}>{winRate.toFixed(0)}%</Typography>
                            </Box>
                          );
                        })}
                        {!sideLoading && !(outcomes?.by_grade || []).length ? (
                          <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', py: 0.55, px: 0.2 }}>{t('dashboard.surgeReadiness.validationPanels.noGradeData')}</Typography>
                        ) : null}
                      </Box>
                    </Box>

                    <Box sx={{ pt: 0.4 }}>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.42, mb: 0.38 }}>
                        {t('dashboard.surgeReadiness.validationPanels.quickBacktest')}
                      </Typography>
                      {backtestLoading ? (
                        <Box sx={{ py: 0.3, display: 'flex', justifyContent: 'center' }}>
                          <CircularProgress size={17} />
                        </Box>
                      ) : backtest?.error ? (
                        <Alert severity="warning">{backtest.error}</Alert>
                      ) : backtest ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.52 }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.52 }}>
                            <StripMetric label={t('dashboard.surgeReadiness.validationPanels.trades')} value={backtest.trade_count} sub={t('dashboard.surgeReadiness.validationPanels.scoreValue', { value: backtest.best_avg_score })} color={C.purple} />
                            <StripMetric label={t('dashboard.surgeReadiness.validationPanels.win5d')} value={`${backtest.win_rate_5d}%`} sub={`${backtest.avg_return_5d}%`} color={backtest.win_rate_5d >= 50 ? C.green : C.red} />
                            <StripMetric label={t('dashboard.surgeReadiness.validationPanels.win10d')} value={`${backtest.win_rate_10d}%`} sub={`${backtest.avg_return_10d}%`} color={backtest.win_rate_10d >= 50 ? C.green : C.red} />
                            <StripMetric label={t('dashboard.surgeReadiness.validationPanels.threshold')} value={`>= ${minScore}`} sub={t('dashboard.surgeReadiness.validationPanels.currentFilter')} color={C.blue} />
                          </Box>
                          {backtest.portfolio_trades > 0 ? (
                            <>
                              <Box sx={{ borderTop: '1px solid #f1f5f9', pt: 0.42 }}>
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.32 }}>
                                  {t('dashboard.surgeReadiness.validationPanels.portfolioSim')}
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.52 }}>
                                  <StripMetric label={t('dashboard.surgeReadiness.validationPanels.cagr')} value={`${backtest.cagr}%`} sub={t('dashboard.surgeReadiness.validationPanels.annualized')} color={backtest.cagr >= 0 ? C.green : C.red} />
                                  <StripMetric label={t('dashboard.surgeReadiness.validationPanels.maxDrawdown')} value={`${backtest.max_drawdown}%`} sub={t('dashboard.surgeReadiness.validationPanels.peakToTrough')} color={C.red} />
                                  <StripMetric label={t('dashboard.surgeReadiness.validationPanels.sharpe')} value={backtest.sharpe} sub={t('dashboard.surgeReadiness.validationPanels.riskAdjReturn')} color={backtest.sharpe >= 1 ? C.green : backtest.sharpe >= 0 ? C.blue : C.red} />
                                  <StripMetric label={t('dashboard.surgeReadiness.validationPanels.profitFactor')} value={backtest.profit_factor} sub={t('dashboard.surgeReadiness.validationPanels.winLossRatio')} color={backtest.profit_factor >= 1.5 ? C.green : C.slate} />
                                  <StripMetric label={t('dashboard.surgeReadiness.validationPanels.avgHold')} value={`${backtest.avg_hold_days}d`} sub={t('dashboard.surgeReadiness.validationPanels.avgHoldDays')} color={C.blue} />
                                  <StripMetric label={t('dashboard.surgeReadiness.validationPanels.portfolioTrades')} value={backtest.portfolio_trades} sub={t('dashboard.surgeReadiness.validationPanels.simTrades')} color={C.purple} />
                                </Box>
                              </Box>
                            </>
                          ) : null}
                        </Box>
                      ) : (
                        <Box sx={{ py: 0.3, px: 0.55, borderRadius: 1, bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.8 }}>
                          <Typography sx={{ fontSize: '0.71rem', color: '#6e7f95' }}>{t('dashboard.surgeReadiness.validationPanels.backtestNotRun')}</Typography>
                          <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: C.blue }}>{`>= ${minScore}`}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Stack>

                  <Box sx={{ pl: { lg: 0.55 }, ml: { lg: 0.15 } }}>
                    <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.42, mb: 0.38 }}>
                      {t('dashboard.surgeReadiness.validationPanels.recentHistory')}
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 0.42 }}>
                      {compactHistory.slice(0, 5).map((row) => (
                        <Box key={row.trade_date} sx={{ py: 0.48, px: 0.55, borderRadius: 1, bgcolor: '#f8fafc', display: 'grid', gridTemplateColumns: 'auto auto 1fr auto', alignItems: 'center', gap: 0.55 }}>
                          <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#334155' }}>{row.trade_date}</Typography>
                          <Chip size="small" label={formatRegimeLabel(t, row.market_regime)} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(regimeColor(row.market_regime), 0.12), color: regimeColor(row.market_regime) }} />
                          <Typography sx={{ fontSize: '0.67rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t('dashboard.surgeReadiness.validationPanels.historyRow', { ticker: row.top_ticker || '—', candidates: row.candidate_count, gradeA: row.grade_a_count })}
                          </Typography>
                          <Typography sx={{ fontSize: '0.67rem', fontWeight: 800, color: '#334155' }}>{row.avg_score || 0}</Typography>
                        </Box>
                      ))}
                      {!compactHistory.length ? (
                        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', py: 0.6 }}>
                          {sideLoading ? t('dashboard.surgeReadiness.validationPanels.loadingHistory') : t('dashboard.surgeReadiness.validationPanels.noHistory')}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </SectionCard>
          </Stack>
        </Box>

        <SectionCard>
          <SectionHeader
            eyebrow={t('dashboard.surgeReadiness.sections.journal.eyebrow')}
            title={t('dashboard.surgeReadiness.sections.journal.title')}
            description={t('dashboard.surgeReadiness.sections.journal.description')}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {[
                    t('dashboard.surgeReadiness.journal.date'),
                    t('dashboard.surgeReadiness.journal.ticker'),
                    t('dashboard.surgeReadiness.journal.grade'),
                    t('dashboard.surgeReadiness.journal.score'),
                    t('dashboard.surgeReadiness.journal.setup'),
                    t('dashboard.surgeReadiness.journal.status'),
                    t('dashboard.surgeReadiness.journal.return5d'),
                    t('dashboard.surgeReadiness.journal.return10d'),
                    t('dashboard.surgeReadiness.journal.outcome10d'),
                  ].map((label) => (
                    <TableCell key={label} sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', py: 1.1, borderBottom: '1px solid #e2e8f0' }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {journal.map((row) => (
                  <TableRow key={`${row.signal_date}-${row.ticker}`} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fcfdff' }, '&:hover': { bgcolor: alpha(C.blue, 0.03) } }}>
                    <TableCell sx={{ fontSize: '0.74rem', borderBottom: '1px solid #f1f5f9' }}>{row.signal_date}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a', cursor: 'pointer' }} onClick={() => openTickerDrawer(row.ticker)}>
                        {row.ticker}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>{row.company_name || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}><Chip size="small" label={row.grade || '—'} sx={{ fontWeight: 700 }} /></TableCell>
                    <TableCell sx={{ fontWeight: 700, color: scoreColor(row.score || 0), borderBottom: '1px solid #f1f5f9' }}>{row.score != null ? row.score.toFixed(1) : '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.74rem', borderBottom: '1px solid #f1f5f9' }}>{row.entry_signal ? formatEntrySignal(t, row.entry_signal) : formatSetupType(t, row.surge_setup_type)}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                      <Chip size="small" label={formatOutcomeStatus(t, row.outcome_status)} sx={{ fontWeight: 700, bgcolor: alpha(C.slate, 0.12), color: C.slate }} />
                    </TableCell>
                    <TableCell sx={{ color: (row.fwd_return_5d || 0) >= 0 ? C.green : C.red, fontWeight: 700, borderBottom: '1px solid #f1f5f9' }}>
                      {row.fwd_return_5d != null ? fmtPct(row.fwd_return_5d * 100, 2) : '—'}
                    </TableCell>
                    <TableCell sx={{ color: (row.fwd_return_10d || 0) >= 0 ? C.green : C.red, fontWeight: 700, borderBottom: '1px solid #f1f5f9' }}>
                      {row.fwd_return_10d != null ? fmtPct(row.fwd_return_10d * 100, 2) : '—'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                      {row.outcome_label_10d ? (
                        <Chip size="small" label={formatOutcomeLabel(t, row.outcome_label_10d)} sx={{ fontWeight: 700, bgcolor: alpha(outcomeColor(row.outcome_label_10d), 0.12), color: outcomeColor(row.outcome_label_10d) }} />
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {!journal.length ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 4, textAlign: 'center', color: '#94a3b8' }}>
                      {sideLoading ? t('dashboard.surgeReadiness.journal.loading') : t('dashboard.surgeReadiness.journal.empty')}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>

        <SurgeDetailDrawer
          ticker={selectedTicker}
          open={Boolean(selectedTicker)}
          onClose={closeTickerDrawer}
          calibration={calibration}
          initialLatest={selectedCandidate}
        />
      </Stack>
    </Box>
  );
}
